import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Download, Check, Clock, ArrowRight, ExternalLink } from "lucide-react";
import PageHero from "../components/PageHero";
import Contact from "../components/Contact";
import { SectionHeading, Eyebrow } from "../components/SectionHeading";
import { admissionSteps, admissionDocs, eligibility } from "../mock/pages";
import { site } from "../mock/mock";
import { logContactEvent } from "../lib/api";
import useSeo from "../hooks/useSeo";

export default function AdmissionsPage() {
  useSeo({
    title: "Admissions 2026-27",
    path: "/admissions",
    description:
      "Admissions open at The KPR Academy, Coimbatore for Pre-KG to Grade VII. Age eligibility, documents required, five-step admission process and admission hotline +91 97906 15551 / +91 97917 15551.",
    keywords:
      "KPR Academy admission, CBSE school admission Coimbatore 2026, Pre-KG admission Coimbatore, school admission hotline Coimbatore, Arasur school admission",
  });
  return (
    <>
      <PageHero
        eyebrow="Admissions Open 2026–27"
        title="Admissions from Pre-KG to Grade VII"
        sub="Call the hotline, message us on WhatsApp, or submit the enquiry form — and we'll arrange a guided campus visit within days."
        crumbs={[{ label: "Admissions" }]}
        image="/campus/school-1.jpg"
      />

      {/* Hotline band */}
      <section className="py-14" style={{ background: "var(--teal-800)" }}>
        <div className="mx-auto grid max-w-[1280px] gap-6 px-5 lg:grid-cols-[1fr_auto] lg:items-center lg:px-6">
          <div className="flex flex-wrap items-center gap-x-10 gap-y-6">
            <div>
              <p className="eyebrow" style={{ color: "var(--gold-200)" }}>
                Admission Hotline
              </p>
              <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
                {site.hotlines.map((h) => (
                  <a
                    key={h}
                    href={`tel:${h.replace(/\s/g, "")}`}
                    onClick={() =>
                      logContactEvent({
                        channel: "call",
                        number: h.replace(/\s/g, ""),
                        display: h,
                        label: "admissions hotline band",
                      })
                    }
                    className="font-display flex items-center gap-3 text-[1.5rem]"
                    style={{ color: "#FBF7F0" }}
                  >
                    <Phone size={18} style={{ color: "var(--gold-400)" }} />
                    {h}
                  </a>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 text-[0.85rem]" style={{ color: "rgba(251,247,240,.7)" }}>
              <Clock size={15} /> Mon – Sat · 9:00 AM to 4:00 PM
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              href={site.applyUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-3 rounded-full px-7 py-4 text-[0.86rem] font-semibold"
              style={{ background: "var(--gold)", color: "#fff", transition: "transform .3s ease" }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "none")}
            >
              <ExternalLink size={16} /> Apply Online
            </a>
            {site.whatsapps.map((w) => (
              <a
                key={w.number}
                href={`https://wa.me/${w.number}?text=${encodeURIComponent(
                  "Hello! I would like to enquire about admissions at The KPR Academy."
                )}`}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  logContactEvent({
                    channel: "whatsapp",
                    number: w.number,
                    display: w.display,
                    message: "Hello! I would like to enquire about admissions at The KPR Academy.",
                    label: "admissions hotline band",
                  })
                }
                className="flex items-center justify-center gap-3 rounded-full px-7 py-4 text-[0.86rem] font-semibold"
                style={{ background: "#1FA855", color: "#fff", transition: "transform .3s ease" }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "none")}
              >
                <MessageCircle size={17} /> WhatsApp {w.display}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 lg:py-28" style={{ background: "var(--cream)" }}>
        <div className="mx-auto max-w-[1280px] px-5 lg:px-6">
          <SectionHeading eyebrow="The process" title="Five simple steps from enquiry to first day" />

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {admissionSteps.map((s, i) => (
              <div
                key={s.no}
                data-reveal=""
                style={{ transitionDelay: `${i * 80}ms`, border: "1px solid rgba(11,59,54,.09)" }}
                className="flex flex-col rounded-2xl bg-white p-6"
              >
                <span className="font-display text-[1.9rem] leading-none" style={{ color: "rgba(192,138,46,.4)" }}>
                  {s.no}
                </span>
                <h3 className="font-display mt-4 text-[1.15rem]" style={{ color: "var(--teal-900)" }}>
                  {s.title}
                </h3>
                <p className="mt-2.5 text-[0.85rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
                  {s.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility + documents */}
      <section className="py-24 lg:py-28" style={{ background: "var(--sand)" }}>
        <div className="mx-auto grid max-w-[1280px] gap-14 px-5 lg:grid-cols-2 lg:gap-20 lg:px-6">
          <div data-reveal="">
            <Eyebrow>Age eligibility</Eyebrow>
            <h2 className="font-display mt-5 text-[2rem] leading-tight" style={{ color: "var(--teal-900)", fontWeight: 400 }}>
              Which grade is my child ready for?
            </h2>
            <div className="mt-8 overflow-hidden rounded-2xl bg-white" style={{ border: "1px solid rgba(11,59,54,.09)" }}>
              {eligibility.map((e, i) => (
                <div
                  key={e.grade}
                  className="flex items-center justify-between px-6 py-4"
                  style={{ borderTop: i ? "1px solid rgba(11,59,54,.07)" : "none" }}
                >
                  <span className="font-display text-[1.05rem]" style={{ color: "var(--teal-900)" }}>
                    {e.grade}
                  </span>
                  <span className="text-[0.85rem]" style={{ color: "var(--muted-ink)" }}>
                    {e.age}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[0.78rem]" style={{ color: "var(--muted-ink)" }}>
              Age is reckoned as on 31st March of the academic year. Final eligibility is confirmed by the school office.
            </p>
          </div>

          <div data-reveal="" style={{ transitionDelay: "120ms" }}>
            <Eyebrow>Documents</Eyebrow>
            <h2 className="font-display mt-5 text-[2rem] leading-tight" style={{ color: "var(--teal-900)", fontWeight: 400 }}>
              What to keep ready
            </h2>
            <div className="mt-8 grid gap-3">
              {admissionDocs.map((d) => (
                <div
                  key={d}
                  className="flex items-center gap-3 rounded-xl bg-white/80 px-5 py-3.5"
                  style={{ border: "1px solid rgba(11,59,54,.08)" }}
                >
                  <span
                    className="grid h-6 w-6 shrink-0 place-items-center rounded-full"
                    style={{ background: "var(--teal-700)", color: "#fff" }}
                  >
                    <Check size={13} strokeWidth={3} />
                  </span>
                  <span className="text-[0.87rem]" style={{ color: "var(--teal-800)" }}>
                    {d}
                  </span>
                </div>
              ))}
            </div>

            <Link
              to="/downloads"
              className="group mt-8 inline-flex items-center gap-3 rounded-full px-7 py-3.5 text-[0.86rem] font-semibold"
              style={{ background: "var(--teal-800)", color: "#FBF7F0" }}
            >
              <Download size={16} /> Printable forms & downloads
              <ArrowRight size={15} className="group-hover:translate-x-1" style={{ transition: "transform .3s ease" }} />
            </Link>
          </div>
        </div>
      </section>

      <Contact />
    </>
  );
}
